<template>
  <div id="overlay" class="show" v-if="isInitialLoading">
    <div id="overlay-container">
      <div class="spinner-border align-items-center">
        <span class="visually-hidden align-items-center">{{ $t('info.loading') }}</span>
      </div>
    </div>
  </div>
  <div>
    <CreateButton :label="$t('workout.newButtonTitle')" @action="changeComponent('create', {})" />
    <filtersBar v-model:filters="filters" />
    <div v-if="workoutsList && workoutsList.length > 0">
      <div
        class="container-lg p-3 bg-body-tertiary mb-5"
        v-for="workoutItem in workoutsList"
        v-bind:key="workoutItem.id">
        <workoutComponent :workoutData="workoutItem" />
        <div class="d-flex p-1">
          <div class="flex-fill" />
          <div class="flex-row-reverse">
            <div class="btn-group">
              <button type="button" class="btn btn-lg btn-outline-dark" @click="downloadXlsx(workoutItem.id)">
                <font-awesome-icon icon="fa-solid fa-file-excel" />
                <span class="d-none d-md-block">{{ $t('buttons.export') }}</span>
              </button>
              <!-- <button type="button" class="btn btn-lg btn-outline-dark" @click="downloadPdf(workoutItem.id)">
                <font-awesome-icon icon="fa-solid fa-file-pdf" />
                <span class="d-none d-md-block">{{ $t('buttons.export') }}</span>
              </button> -->
              <button type="button" class="btn btn-lg btn-outline-dark" @click="changeComponent('edit', workoutItem)">
                <font-awesome-icon icon="fa-solid fa-sliders" />
                <span class="d-none d-md-block">{{ $t('buttons.edit') }}</span>
              </button>
              <button type="button" class="btn btn-lg btn-outline-dark" @click="cloneWorkout(workoutItem)">
                <font-awesome-icon icon="fa-solid fa-clone" />
                <span class="d-none d-md-block">{{ $t('buttons.clone') }}</span>
              </button>
            </div>
          </div>
        </div>
        <hr>
      </div>
      <div ref="sentinel" aria-hidden="true" style="height: 1px;" />
      <div v-if="isFetchingMore" class="text-center py-3">
        <div class="spinner-border">
          <span class="visually-hidden">{{ $t('info.loading') }}</span>
        </div>
      </div>
      <div v-if="isError && !isInitialLoading" class="text-center py-3">
        <div class="alert alert-danger mb-2">{{ errorMsg || $t('errorsMsg.failedMsg') }}</div>
        <button class="btn btn-outline-dark" type="button" @click="loadMoreFeed(false)">
          {{ $t('buttons.retry') || 'Retry' }}
        </button>
      </div>
      <div v-if="isEndOfList && workoutsList.length > 1 && !isFetchingMore" class="text-center text-muted py-3">
        {{ $t('info.endOfList') || 'End of list' }}
      </div>
    </div>
    <div v-else>
      <div class="alert alert-danger" role="alert" v-if="errorData.msg">
        {{ errorData.msg }}
      </div>
      <div v-else-if="!isInitialLoading && !isFetchingMore" class="container-lg p-3 bg-body-tertiary text-center">
        <template v-if="route.name === 'today'">
          <h1 class="display-1">{{ $t('errorsMsg.noWorkoutToday') }}</h1>
          <div class="btn-group">
            <button
              v-if="workoutPrev && workoutPrev.PublishedAt"
              class=" btn btn-outline-dark"
              type="button"
              @click="changeComponent('edit', workoutPrev)"
              data-bs-slide="prev">
              <span>
                <font-awesome-icon icon="fa-solid fa-arrow-left" />
                {{ new Date(workoutPrev.PublishedAt).toISOString().split('T')[0] }}
              </span>
            </button>
            <button
              v-if="workoutNext && workoutNext.PublishedAt"
              class=" btn btn-outline-dark"
              type="button"
              @click="changeComponent('edit', workoutNext)"
              data-bs-slide="next">
              <span>
                {{ $t('workout.plannedOn') }} {{ new Date(workoutNext.PublishedAt).toISOString().split('T')[0] }}
                <font-awesome-icon icon="fa-solid fa-arrow-right" />
              </span>
            </button>
          </div>
        </template>
        <template v-else>
          <h1 class="display-1">{{ $t('errorsMsg.noDataAvailable') }}</h1>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, watch, onMounted, onUnmounted,
} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  getAllWorkouts,
  getTodayWorkout,
  downloadXlsxWorkout,
} from '@/api/api';
import { useUserStore } from '@/stores/userStore';
import useChangeComponent from '@/composables/useChangeComponent';
import workoutComponent from './workoutPage.vue';
import filtersBar from './components/filtersBar.vue';
import CreateButton from '../common/addWorkoutButton.vue';

const emit = defineEmits(['change-component']);

const route = useRoute();
const { t } = useI18n();
const { changeComponent } = useChangeComponent(emit);
const userStore = useUserStore();

const workoutsList = ref([]);
const isInitialLoading = ref(false);
const isFetchingMore = ref(false);
const isError = ref(false);
const errorMsg = ref('');
const isEndOfList = ref(false);

const page = ref(1);

const filters = ref({
  muscle: '',
  gymType: '',
  date: '',
  exerciseName: '',
  cardioType: '',
});

const workoutPrev = ref([]);
const workoutNext = ref([]);
const errorData = ref({ code: '', msg: '' });

const sentinel = ref(null);
let observer = null;

function handleError(error, fallbackMsg = 'errorsMsg.failedMsg') {
  console.error(error);
  errorData.value = {
    code: error?.response?.status || '',
    msg: error?.message || String(error),
  };
  if (globalThis.$toast?.showToast) {
    globalThis.$toast.showToast(t(fallbackMsg), 'danger');
  }
}

async function loadMoreFeed(isInitial = false) {
  if (isFetchingMore.value || isEndOfList.value || isError.value) return;
  if (route.name !== 'feed') return;

  if (isInitial) {
    isInitialLoading.value = true;
  } else {
    isFetchingMore.value = true;
  }
  isError.value = false;
  errorMsg.value = '';

  try {
    const queryParams = {
      page: page.value,
      muscle: filters.value.muscle || undefined,
      gymType: filters.value.gymType || undefined,
      date: filters.value.date || undefined,
      exerciseName: filters.value.exerciseName || undefined,
      cardioType: filters.value.cardioType || undefined,
    };
    const { data } = await getAllWorkouts(queryParams);
    const chunk = data.data || [];
    workoutsList.value.push(...chunk);

    if (chunk.length === 0 || page.value >= data.last_page) {
      isEndOfList.value = true;
    } else {
      page.value += 1;
    }
    errorData.value = { code: '', msg: '' };
  } catch (e) {
    isError.value = true;
    errorMsg.value = e?.message || 'Failed to load';
    handleError(e);
  } finally {
    if (isInitial) {
      isInitialLoading.value = false;
    } else {
      isFetchingMore.value = false;
    }
  }
}

async function resetAndLoadFeed() {
  workoutsList.value = [];
  page.value = 1;
  isEndOfList.value = false;
  isError.value = false;
  errorMsg.value = '';
  await loadMoreFeed(true);
}

async function loadToday() {
  isInitialLoading.value = true;
  isError.value = false;
  errorMsg.value = '';
  isEndOfList.value = true;
  workoutsList.value = [];
  workoutNext.value = [];
  workoutPrev.value = [];
  errorData.value = { code: '', msg: '' };
  try {
    const { data } = await getTodayWorkout();
    workoutsList.value = data.data || [];
    workoutNext.value = data.next || [];
    workoutPrev.value = data.prev || [];
    errorData.value = { code: '', msg: '' };
  } catch (error) {
    handleError(error);
  } finally {
    isInitialLoading.value = false;
  }
}

function cloneWorkout(workout) {
  const cloned = {
    ...workout,
    id: null,
    PublishedAt: null,
    review: {
      ...workout.review,
      ai_conclusion: null,
    },
  };
  changeComponent('edit', cloned);
}

async function downloadXlsx(docId) {
  try {
    const response = await downloadXlsxWorkout({ id: docId, lang: userStore.lang });
    if (response.status !== 200) {
      throw new Error(`Unexpected status: ${response.status}`);
    }
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const contentDisposition = response.headers['content-disposition'];
    const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'document.xlsx';

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error, 'errorsMsg.downloadFailed');
  }
}

function teardownObserver() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}

function setupObserver() {
  teardownObserver();
  if (!sentinel.value) return;
  observer = new IntersectionObserver((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      loadMoreFeed(false);
    }
  }, {
    root: null,
    rootMargin: '200px 0px',
    threshold: 0,
  });
  observer.observe(sentinel.value);
}

function handleRoute(name) {
  if (name === 'today') {
    loadToday();
    teardownObserver();
  } else if (name === 'feed') {
    resetAndLoadFeed();
  } else {
    console.warn('Unexpected route', name);
  }
}

watch(sentinel, (el) => {
  if (el) {
    setupObserver();
  } else {
    teardownObserver();
  }
}, { flush: 'post' });

watch(filters, () => {
  if (route.name === 'feed') {
    resetAndLoadFeed();
  }
}, { deep: true });

watch(() => route.name, (name) => {
  handleRoute(name);
});

onMounted(() => {
  handleRoute(route.name);
});

onUnmounted(() => {
  teardownObserver();
});
</script>

<style scoped>
body {
  overflow: hidden;
}

#overlay {
  background-color: rgba(255, 255, 255, 0.7);
  display: none;
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
}

#overlay.show {
  display: block;
}

#overlay-container {
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
